import { Plugin } from '@uppy/core';
import Translator from '@uppy/utils/lib/Translator'; // eslint-disable-line
import axios from 'axios';
import _ from 'lodash';
import { timer } from '@utilities/helpers'; // eslint-disable-line
import VideoTranscoderService from '../services/VideoTranscoderService';

class UppyPlaypositCloudflarePlugin extends Plugin {
  constructor(uppy, opts = {}) {
    super(uppy, opts);
    this.id = opts.id || 'PlaypositPlugin';
    this.teacherId = opts.teacherId;
    this.studentId = opts.studentId;
    this.headers = opts.headers;
    this.pollingInterval = opts.pollingInterval || 15000;
    this.pollingTimeout = opts.pollingTimeout || 1000000;
    this.type = 'modifier';
    this.defaultLocale = {
      strings: {
        initializingPlaypositVideo: 'Initializing PlayPosit video...',
        updatingPlaypositVideo: 'Updating PlayPosit video...',
        queuedForTranscoding: 'Queued for transcoding',
        transcoding: 'Transcoding...',
        finalizingVideo: 'Finalizing video...',
      },
    };

    this.i18nInit();
    this.prepareUpload = this.prepareUpload.bind(this);
    this.handleUploadCompletion = this.handleUploadCompletion.bind(this);
    this.handleCancellation = this.handleCancellation.bind(this);
    this.poll = this.poll.bind(this);
  }

  setOptions(newOpts) {
    super.setOptions(newOpts);
    this.i18nInit();
  }

  i18nInit() {
    this.translator = new Translator([
      this.defaultLocale,
      this.uppy.locale,
      this.opts.locale,
    ]);
    this.i18n = this.translator.translate.bind(this.translator);
    this.setPluginState();
  }

  generateCancelToken() {
    this.cancelTokenSource = axios.CancelToken.source();
  }

  cancelRequests() {
    if (_.isFunction(this?.cancelTokenSource?.cancel)) this.cancelTokenSource.cancel();
  }

  async handleCancellation() {
    this.cancelRequests();
    const unfinishedFiles = (this.uppy.getFiles() || []).filter((file) => (
      !file
      || !file.isFinished
    ));
    if (!unfinishedFiles.length) return;
    await Promise.all(unfinishedFiles.map((file) => VideoTranscoderService.updateHostingLog({
      id: file?.hostingLog?.id,
      cloudflareStatus: 'canceled',
    })));
  }

  async prepareUpload(fileIds) {
    const promises = fileIds.map(async (fileId) => {
      try {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('preprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('initializingPlaypositVideo'),
        });
        if (file.type.split('/')[0] !== 'video') return;
        this.generateCancelToken();
        const response = await VideoTranscoderService.initializeTranscode({
          name: file.name,
          teacherId: this.teacherId,
          studentId: this.studentId,
        }, { cancelToken: this.cancelTokenSource.token });
        const {
          video: playpositVideo,
          hostingLog,
        } = response.data;
        this.uppy.setFileState(fileId, {
          hostingLog,
          playpositVideo,
        });
      } catch(e) {
        this.uppy.log('[Uppy PlayPosit Cloudflare] Failed to initialize a video:', 'warning');
        this.uppy.log(e, 'warning');
      }
    });

    const emitPreprocessCompleteForAll = () => {
      for (const fileId of fileIds) {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('preprocess-complete', file);
      }
    };

    return Promise.all(promises)
      .then(emitPreprocessCompleteForAll);
  }

  poll(fn, file) {
    const endTime = Number(new Date()) + this.pollingTimeout;
    let hasDuration = false;
    let timeoutId;

    function clearPollingTimeout() {
      clearTimeout(timeoutId);
    }

    this.uppy.off('file-removed', clearPollingTimeout);
    this.uppy.off('cancel-all', clearPollingTimeout);
    this.uppy.on('file-removed', clearPollingTimeout);
    this.uppy.on('cancel-all', clearPollingTimeout);

    const checkCondition = async (resolve, reject) => {
      try {
        const response = await fn();
        if (_.get(response, 'data.result.status.state') === 'error') {
          reject(new Error('An error occurred during the transcoding process.'));
        }
        if (
          !hasDuration
          && _.isFinite(_.get(response, 'data.result.duration'))
        ) {
          hasDuration = true;
          await VideoTranscoderService.updateTranscode({
            videoId: file.playpositVideo.id,
            duration: response.data.result.duration,
            teacherId: this.teacherId,
            studentId: this.studentId,
          }, { cancelToken: this.cancelTokenSource.token });
        }
        if (_.get(response, 'data.result.status.pctComplete')) {
          this.uppy.emit('postprocess-progress', file, {
            mode: 'determinate',
            message: this.i18n('transcoding'),
            value: parseFloat(response.data.result.status.pctComplete) / 100,
          });
        }
        if (_.get(response, 'data.result.status.state') === 'ready') {
          this.uppy.emit('postprocess-progress', file, {
            mode: 'determinate',
            message: this.i18n('transcoding'),
            value: 1,
          });
          resolve(response.data);
        } else if (Number(new Date()) < endTime) {
          timeoutId = setTimeout(checkCondition, this.pollingInterval, resolve, reject);
        } else {
          reject(new Error('Timed out.'));
        }
      } catch(e) {
        if (_.get(e, 'response.status') === 404) {
          timeoutId = setTimeout(checkCondition, this.pollingInterval, resolve, reject);
        } else {
          clearPollingTimeout();
          reject(e);
        }
      }
    };

    return new Promise(checkCondition);
  }

  async handleUploadCompletion(fileIds) {
    const promises = fileIds.map(async (fileId) => {
      try {
        const file = this.uppy.getFile(fileId);
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('queuedForTranscoding'),
          value: 0,
        });
        const cloudflareId = file.uploadURL.split('/').reverse()[0].split('?')[0];
        await Promise.all([
          VideoTranscoderService.updateTranscode({
            videoId: file.playpositVideo.id,
            url: `https://stream.playposit.com/${cloudflareId}/manifest/video.m3u8`,
            thumbnail: `https://stream-thumbnails.playposit.com/${cloudflareId}/thumbnails/thumbnail.jpg`,
            teacherId: this.teacherId,
            studentId: this.studentId,
            cloudflareId,
            cloudflareStatus: 'transcoding',
          }, { cancelToken: this.cancelTokenSource.token }),
          VideoTranscoderService.createTeacherVideo({
            name: file.name,
            thumbnail: `https://stream-thumbnails.playposit.com/${cloudflareId}/thumbnails/thumbnail.jpg`,
            videoId: file.hostingLog.videoId,
            teacherId: this.teacherId,
          }, { cancelToken: this.cancelTokenSource.token }),
        ]);
        this.uppy.emit('teacher-video-created');
        const pollData = await this.poll(() => axios.get(
          `https://api.stream.playposit.com/client/v4/accounts/ad6798efefe74f7b6198af66a777584c/stream/${cloudflareId}`,
          {
            headers: {
              'X-Auth-Email': 'ben@playposit.org',
            },
            transformRequest: [(data, headers) => {
              delete headers.common.Authorization;
              return data;
            }],
          },
        ), file);
        this.uppy.emit('postprocess-progress', file, {
          mode: 'indeterminate',
          message: this.i18n('finalizingVideo'),
          value: 0,
        });
        const [{ data: updatedPlaypositVideo }] = await Promise.all([
          VideoTranscoderService.updateTranscode({
            videoId: file.playpositVideo.id,
            duration: _.get(pollData, 'result.duration', 0),
            teacherId: this.teacherId,
            studentId: this.studentId,
            cloudflareStatus: 'ready',
          }, { cancelToken: this.cancelTokenSource.token }),
          timer(10000),
        ]);
        this.uppy.setFileState(fileId, {
          playpositVideo: updatedPlaypositVideo,
          pollData,
          isFinished: true,
        });
      } catch(e) {
        this.uppy.log('[Uppy PlayPosit Cloudflare] Failed to update a video:', 'warning');
        this.uppy.log(e, 'warning');
        this.uppy.cancelAll();
        this.uppy.emit('error', e);
      }
    });

    const emitPostprocessCompleteForAll = () => {
      for (const fileId of fileIds) {
        const file = this.uppy.getFile(fileId);
        if (file) this.uppy.emit('postprocess-complete', file);
      }
    };

    return Promise.all(promises)
      .then(emitPostprocessCompleteForAll);
  }

  install() {
    this.uppy.addPreProcessor(this.prepareUpload);
    this.uppy.addPostProcessor(this.handleUploadCompletion);
    this.uppy.on('cancel-all', this.handleCancellation);
  }

  uninstall() {
    this.uppy.removePreProcessor(this.prepareUpload);
    this.uppy.removePostProcessor(this.handleUploadCompletion);
    this.uppy.off('cancel-all', this.handleCancellation);
  }
}

export default UppyPlaypositCloudflarePlugin;